import { useNetworkState } from '@lbcamden/lib-react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { webAuthnEnabled } from '~/client/config/appConfig';
import { routes } from '~/client/config/routes';
import {
  useClientAuthInfo,
  useClientPinAuthInfo,
  useIsUserRegistered,
} from '~/client/hooks/authHooks';
import { useCurrentUser } from '~/client/hooks/storeHooks';
import { api } from '~/client/lib/trpc';
import { useAppStore } from '~/client/store/app/appStore';

const nonRedirectRoutes: string[] = [routes.login, routes.error, routes.authFailed];

const useClientAuth = webAuthnEnabled ? useClientAuthInfo : useClientPinAuthInfo;

export const useEnsureHasRegisteredUser = () => {
  const { online } = useNetworkState();

  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const { storeUsername } = useClientAuth();

  const user = useCurrentUser();

  const setLoginRedirectUri = useAppStore((state) => state.setLoginRedirectUri);
  const setUser = useAppStore((state) => state.setUser);

  const { data: retrievedUser, isError } = api.user.useQuery(undefined, {
    enabled: !user,
    retry: false,
  });

  const { isRegistered, checkingRegistration } = useIsUserRegistered();

  const canRetrieveUser = online && !isError;
  const requiresRegistration = user && !isRegistered && !checkingRegistration;

  useEffect(() => {
    if (user || canRetrieveUser) {
      return;
    }
    if (!nonRedirectRoutes.includes(pathname)) {
      setLoginRedirectUri(`${pathname}${search}`);
      void navigate(routes.login);
    }
  }, [user, isError, pathname]);

  useEffect(() => {
    if (requiresRegistration && pathname !== routes.authRegistration) {
      void navigate(routes.authRegistration, { replace: true });
    }
  }, [requiresRegistration, pathname]);

  useEffect(() => {
    if (retrievedUser) {
      setUser(retrievedUser);
    }
  }, [retrievedUser]);

  useEffect(() => {
    if (user) {
      storeUsername(user.email);
    }
  }, [user]);
};
